import { useState, useContext, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  alpha,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import RouteIcon from '@mui/icons-material/Route';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ScheduleIcon from '@mui/icons-material/Schedule';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditNoteIcon from '@mui/icons-material/EditNote';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import { DuplicateRouteDialog } from './DuplicateRouteDialog';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';

interface DeliveryRoute {
  id: string;
  name: string;
  status: 'draft' | 'planned' | 'in_progress' | 'completed' | 'cancelled';
  driverId: string | null;
  driver: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  originLocation: {
    id: string;
    name: string;
  };
  deliveryIds: string[];
  totalDistanceKm: number;
  totalDurationMinutes: number;
  returnToOrigin: boolean;
  trafficAware: boolean;
  isFavorite: boolean;
  scheduledDate: string | null;
  departureTime: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  routeData: {
    waypoints: {
      deliveryId: string;
      lat: number;
      lng: number;
      address?: string;
    }[];
  };
}

const fetchRoutes = async (): Promise<DeliveryRoute[]> => {
  const response = await axiosInstance.get('/delivery-routes');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const updateRouteStatus = async ({
  id,
  status,
}: {
  id: string;
  status: string;
}): Promise<DeliveryRoute> => {
  const response = await axiosInstance.patch(`/delivery-routes/${id}/status`, { status });
  return response.data;
};

const toggleFavorite = async (id: string): Promise<DeliveryRoute> => {
  const response = await axiosInstance.patch(`/delivery-routes/${id}/favorite`);
  return response.data;
};

export const RoutesTab = () => {
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; routeId: string } | null>(null);
  const [duplicateDialogRoute, setDuplicateDialogRoute] = useState<DeliveryRoute | null>(null);

  const { data: routes = [], isLoading } = useQuery({
    queryKey: ['delivery-routes'],
    queryFn: fetchRoutes,
  });

  const statusMutation = useMutation({
    mutationFn: updateRouteStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-routes'] });
      showMessage('Route status updated', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update route status', 'error');
    },
  });

  const favoriteMutation = useMutation({
    mutationFn: toggleFavorite,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-routes'] });
      showMessage('Favorite updated', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update favorite', 'error');
    },
  });

  const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      // Search filter
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const matchesSearch =
          route.name?.toLowerCase().includes(search) ||
          route.originLocation?.name?.toLowerCase().includes(search) ||
          route.driver?.firstName?.toLowerCase().includes(search) ||
          route.driver?.lastName?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (statusFilter && route.status !== statusFilter) {
        return false;
      }

      return true;
    });
  }, [routes, searchValue, statusFilter]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = routes.length;
    const draft = routes.filter((r) => r.status === 'draft').length;
    const planned = routes.filter((r) => r.status === 'planned').length;
    const inProgress = routes.filter((r) => r.status === 'in_progress').length;
    const completed = routes.filter((r) => r.status === 'completed').length;
    const cancelled = routes.filter((r) => r.status === 'cancelled').length;
    return { total, draft, planned, inProgress, completed, cancelled };
  }, [routes]);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'draft':
        return palette.accent;
      case 'planned':
        return palette.info;
      case 'in_progress':
        return palette.warning;
      case 'completed':
        return palette.success;
      case 'cancelled':
        return palette.textMuted;
      default:
        return palette.textMuted;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return <EditNoteIcon sx={{ fontSize: 16 }} />;
      case 'planned':
        return <ScheduleIcon sx={{ fontSize: 16 }} />;
      case 'in_progress':
        return <PlayArrowIcon sx={{ fontSize: 16 }} />;
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      case 'cancelled':
        return <CancelIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  const formatStatusLabel = (status: string): string => {
    return status.replace(/_/g, ' ').toUpperCase();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return { date: '-', time: '' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('fr-DZ', { day: '2-digit', month: 'short', year: 'numeric' }),
      time: date.toLocaleTimeString('fr-DZ', { hour: '2-digit', minute: '2-digit' }),
    };
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, routeId: string) => {
    setMenuAnchor({ el: event.currentTarget, routeId });
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleStatusChange = (status: string) => {
    if (menuAnchor) {
      statusMutation.mutate({ id: menuAnchor.routeId, status });
      handleMenuClose();
    }
  };

  const getAvailableStatusActions = (currentStatus: string, route?: DeliveryRoute) => {
    switch (currentStatus) {
      case 'draft':
        // Draft routes need driver and scheduled date before they can transition
        return [
          { status: 'cancelled', label: 'Cancel Route', icon: <CancelIcon /> },
        ];
      case 'planned':
        return [
          { status: 'in_progress', label: 'Start Route', icon: <PlayArrowIcon /> },
          { status: 'cancelled', label: 'Cancel Route', icon: <CancelIcon /> },
        ];
      case 'in_progress':
        return [
          { status: 'completed', label: 'Mark Completed', icon: <CheckCircleIcon /> },
          { status: 'cancelled', label: 'Cancel Route', icon: <CancelIcon /> },
        ];
      default:
        return [];
    }
  };

  const paginatedRoutes = filteredRoutes.slice(page * pageSize, page * pageSize + pageSize);

  const colors =
    mode === 'dark'
      ? {
          bg: palette.background,
          surface: palette.surface,
          surfaceElevated: palette.surfaceElevated,
          border: palette.border,
          borderLight: palette.borderLight,
          textPrimary: palette.textPrimary,
          textSecondary: palette.textSecondary,
          textMuted: palette.textMuted,
        }
      : {
          bg: palette.light.background,
          surface: palette.light.surface,
          surfaceElevated: palette.light.surfaceElevated,
          border: palette.light.border,
          borderLight: palette.light.borderLight,
          textPrimary: palette.light.textPrimary,
          textSecondary: palette.light.textSecondary,
          textMuted: palette.light.textMuted,
        };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Stats Dashboard */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 2,
          mb: 3,
          ...animations.slideUp(0.1),
        }}
      >
        {[
          { label: 'TOTAL ROUTES', value: stats.total, color: palette.primary },
          { label: 'DRAFT', value: stats.draft, color: palette.accent },
          { label: 'PLANNED', value: stats.planned, color: palette.info },
          { label: 'IN PROGRESS', value: stats.inProgress, color: palette.warning },
          { label: 'COMPLETED', value: stats.completed, color: palette.success },
          { label: 'CANCELLED', value: stats.cancelled, color: palette.textMuted },
        ].map((stat) => (
          <Card
            key={stat.label}
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2,
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 8px 24px ${alpha(stat.color, 0.2)}`,
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: stat.color,
              },
            }}
            onClick={() => {
              if (stat.label !== 'TOTAL ROUTES') {
                setStatusFilter(stat.label.toLowerCase().replace(' ', '_'));
              } else {
                setStatusFilter('');
              }
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: '10px',
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.extrabold,
                color: stat.color,
                lineHeight: 1,
                mt: 0.5,
              }}
            >
              {stat.value}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Search & Filter Bar */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
          ...animations.slideUp(0.2),
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="SEARCH ROUTES..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
                '&:hover': {
                  borderColor: colors.borderLight,
                },
                '&.Mui-focused': {
                  borderColor: palette.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'uppercase',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
          {statusFilter && (
            <Chip
              label={`Status: ${formatStatusLabel(statusFilter)}`}
              onDelete={() => setStatusFilter('')}
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                textTransform: 'uppercase',
              }}
            />
          )}
        </Box>
      </Card>

      {/* Routes Table */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.3),
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: palette.primary }} />
          </Box>
        ) : filteredRoutes.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <RouteIcon sx={{ fontSize: 80, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                mb: 1,
              }}
            >
              NO ROUTES FOUND
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              {routes.length === 0
                ? 'Create your first route from the "To Schedule" tab'
                : 'No routes match your search criteria'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-root': {
                    borderBottom: `1px solid ${colors.border}`,
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: colors.surfaceElevated,
                      borderBottom: `2px solid ${palette.primary}`,
                    }}
                  >
                    {[
                      'Route Name',
                      'Status',
                      'Driver',
                      'Origin',
                      'Deliveries',
                      'Distance',
                      'Duration',
                      'Created',
                      'Actions',
                    ].map((header, index) => (
                      <TableCell
                        key={header}
                        align={index === 8 ? 'center' : 'left'}
                        sx={{
                          color: colors.textMuted,
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.xs,
                          fontWeight: typography.fontWeight.bold,
                          textTransform: 'uppercase',
                          letterSpacing: typography.letterSpacing.widest,
                          py: 2,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRoutes.map((route, index) => {
                    const statusColor = getStatusColor(route.status);
                    const createdAt = formatDateTime(route.createdAt);

                    return (
                      <TableRow
                        key={route.id}
                        sx={{
                          transition: theme.transitions.fast,
                          borderLeft: `4px solid ${statusColor}`,
                          '&:hover': {
                            backgroundColor: alpha(statusColor, 0.05),
                          },
                          ...animations.tableRowFade(index),
                        }}
                      >
                        {/* Route Name */}
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title={route.isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  favoriteMutation.mutate(route.id);
                                }}
                                disabled={favoriteMutation.isPending}
                                sx={{
                                  color: route.isFavorite ? '#F59E0B' : colors.textMuted,
                                  p: 0.5,
                                  '&:hover': {
                                    color: '#F59E0B',
                                    backgroundColor: alpha('#F59E0B', 0.1),
                                  },
                                }}
                              >
                                {route.isFavorite ? (
                                  <StarIcon sx={{ fontSize: 18 }} />
                                ) : (
                                  <StarBorderIcon sx={{ fontSize: 18 }} />
                                )}
                              </IconButton>
                            </Tooltip>
                            <RouteIcon sx={{ fontSize: 18, color: palette.primary }} />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                color: colors.textPrimary,
                                fontSize: typography.fontSize.sm,
                                fontWeight: typography.fontWeight.bold,
                              }}
                            >
                              {route.name || `Route ${route.id.substring(0, 8).toUpperCase()}`}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(route.status) || undefined}
                            label={formatStatusLabel(route.status)}
                            sx={{
                              backgroundColor: alpha(statusColor, 0.1),
                              color: statusColor,
                              fontFamily: typography.fontFamily.mono,
                              fontSize: '10px',
                              fontWeight: typography.fontWeight.bold,
                              border: `1px solid ${statusColor}`,
                              '& .MuiChip-icon': {
                                color: statusColor,
                              },
                            }}
                          />
                        </TableCell>

                        {/* Driver */}
                        <TableCell>
                          {route.driver ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  backgroundColor: palette.info,
                                  fontSize: '12px',
                                }}
                              >
                                {route.driver.firstName?.[0]}
                                {route.driver.lastName?.[0]}
                              </Avatar>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: typography.fontFamily.display,
                                  color: colors.textPrimary,
                                  fontSize: typography.fontSize.sm,
                                }}
                              >
                                {route.driver.firstName} {route.driver.lastName}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip
                              icon={<PersonIcon sx={{ fontSize: 14 }} />}
                              label="UNASSIGNED"
                              size="small"
                              sx={{
                                backgroundColor: alpha(palette.warning, 0.1),
                                color: palette.warning,
                                fontFamily: typography.fontFamily.mono,
                                fontSize: '10px',
                                fontWeight: typography.fontWeight.bold,
                                border: `1px solid ${palette.warning}`,
                                '& .MuiChip-icon': {
                                  color: palette.warning,
                                },
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Origin */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: palette.info }} />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.display,
                                color: colors.textPrimary,
                                fontSize: typography.fontSize.sm,
                                maxWidth: 150,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {route.originLocation?.name || 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Deliveries */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalShippingIcon sx={{ fontSize: 16, color: palette.primary }} />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: typography.fontFamily.mono,
                                color: colors.textPrimary,
                                fontSize: typography.fontSize.sm,
                                fontWeight: typography.fontWeight.bold,
                              }}
                            >
                              {route.deliveryIds?.length || 0}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Distance */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              color: colors.textPrimary,
                              fontSize: typography.fontSize.sm,
                            }}
                          >
                            {route.totalDistanceKm} km
                          </Typography>
                        </TableCell>

                        {/* Duration */}
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: typography.fontFamily.mono,
                              color: colors.textPrimary,
                              fontSize: typography.fontSize.sm,
                            }}
                          >
                            {route.totalDurationMinutes} min
                          </Typography>
                        </TableCell>

                        {/* Created */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccessTimeIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: typography.fontFamily.display,
                                  fontWeight: typography.fontWeight.semibold,
                                  color: colors.textPrimary,
                                  fontSize: typography.fontSize.sm,
                                  lineHeight: 1.2,
                                }}
                              >
                                {createdAt.date}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontFamily: typography.fontFamily.mono,
                                  color: colors.textMuted,
                                  fontSize: typography.fontSize.xs,
                                }}
                              >
                                {createdAt.time}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/app/deliveries/routes/${route.id}`)}
                                sx={{
                                  border: `1px solid ${colors.border}`,
                                  borderRadius: theme.radius.xs,
                                  color: colors.textPrimary,
                                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                  '&:hover': {
                                    borderColor: palette.primary,
                                    backgroundColor: alpha(palette.primary, 0.1),
                                    color: palette.primary,
                                    transform: 'scale(1.1)',
                                  },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Duplicate Route">
                              <IconButton
                                size="small"
                                onClick={() => setDuplicateDialogRoute(route)}
                                sx={{
                                  border: `1px solid ${colors.border}`,
                                  borderRadius: theme.radius.xs,
                                  color: colors.textPrimary,
                                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                  '&:hover': {
                                    borderColor: palette.accent,
                                    backgroundColor: alpha(palette.accent, 0.1),
                                    color: palette.accent,
                                    transform: 'scale(1.1)',
                                  },
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {(route.status === 'draft' || route.status === 'planned' || route.status === 'in_progress') && (
                              <Tooltip title="More Actions">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, route.id)}
                                  sx={{
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: theme.radius.xs,
                                    color: colors.textPrimary,
                                    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                    '&:hover': {
                                      borderColor: palette.primary,
                                      backgroundColor: alpha(palette.primary, 0.1),
                                      color: palette.primary,
                                    },
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                backgroundColor: colors.surfaceElevated,
                borderTop: `2px solid ${colors.border}`,
              }}
            >
              <TablePagination
                component="div"
                count={filteredRoutes.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    color: colors.textPrimary,
                    fontFamily: typography.fontFamily.mono,
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.xs,
                    fontWeight: typography.fontWeight.bold,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterSpacing.wide,
                  },
                  '& .MuiSelect-select': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                    fontWeight: typography.fontWeight.bold,
                  },
                }}
              />
            </Box>
          </>
        )}
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: theme.radius.sm,
          },
        }}
      >
        {menuAnchor &&
          getAvailableStatusActions(
            routes.find((r) => r.id === menuAnchor.routeId)?.status || ''
          ).map((action) => (
            <MenuItem
              key={action.status}
              onClick={() => handleStatusChange(action.status)}
              disabled={statusMutation.isPending}
            >
              <ListItemIcon sx={{ color: getStatusColor(action.status) }}>
                {action.icon}
              </ListItemIcon>
              <ListItemText
                primary={action.label}
                sx={{
                  '& .MuiTypography-root': {
                    fontFamily: typography.fontFamily.mono,
                    fontSize: typography.fontSize.sm,
                  },
                }}
              />
            </MenuItem>
          ))}
      </Menu>

      {/* Duplicate Route Dialog */}
      <DuplicateRouteDialog
        open={duplicateDialogRoute !== null}
        onClose={() => setDuplicateDialogRoute(null)}
        route={duplicateDialogRoute}
      />
    </Box>
  );
};

export default RoutesTab;
